import Image from "@Components/Image";
import { pageAsset } from "@Utils/getStaticAssetPath";
import styles from "./AmbassadorPanel.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

const asset = pageAsset("AmbassadorV2");

/**
 * The lawn-and-sky plate supplied for this section. 1153x1600 at q68 (111 KB) —
 * the slot renders around 520px wide, so this is comfortably 2x for retina and
 * well inside the repo's 250 KB pre-commit gate on images.
 */
const BACKDROP = asset("why-join-bg.jpg");

interface Ambassador {
  handle: string;
  /** Follower count exactly as Instagram writes it — see the note below. */
  followers: string;
  /**
   * REQUIRED, deliberately. Every row carries one of the supplied headshots, so
   * there is no placeholder branch and no monogram fallback: the type refuses a
   * row with no picture rather than letting a low-res Instagram thumbnail or a
   * grey initial back into the list.
   */
  avatar: string;
}

/**
 * The five ambassadors on the card.
 *
 * ── Where this data came from ──────────────────────────────────────────────
 * Handles and follower counts were read out of each creator's own Instagram
 * embed in a headless browser — the same harvest that built the hero rail, see
 * `ReelsRail/reels.ts`. The headshots were supplied. Nothing is invented, and no
 * name, campus or figure is made up.
 *
 * ⚠️  WHY FIVE. The supplied headshots cover exactly these five people. The list
 * used to also carry `jaystravelvlogs` and `boss_ellarh`, who have no headshot —
 * and leaving them on their 150x150 Instagram thumbnails beside five studio
 * crops looked worse than dropping them. Their reels are still in the hero rail.
 *
 * There were nine unique accounts in the harvest, and the card used to print a
 * "5 of 9" count saying so. That has been removed on request, so the card no
 * longer discloses a total — it presents five ambassadors and makes no claim
 * about being the whole roster either way.
 *
 * ⚠️  DELIBERATELY NOT IN RANK ORDER. Earlier passes sorted by follower count and
 * read as a leaderboard, which is the wrong argument — the section says a cohort
 * of students does this, not that they compete with each other. Big and small
 * alternate down the list on purpose. Do not "fix" this by sorting it.
 *
 * ⚠️  FOLLOWER COUNTS ARE A SNAPSHOT, captured Sep 2026, written the way Instagram
 * writes them — which is why "9,659" is not rounded to match the others.
 * Rounding would be inventing precision, and summing them into a "combined
 * reach" figure would be worse: most are already rounded to one significant
 * figure, so any total would be fiction — which is also why nothing here is
 * aggregated now that the count is gone.
 */
const AMBASSADORS: Ambassador[] = [
  { handle: "rclangeline", followers: "2K", avatar: asset("faces/rclangeline.jpg") },
  { handle: "damolaa_x", followers: "98K", avatar: asset("faces/damolaa_x.jpg") },
  { handle: "itsmeameliaa._", followers: "4K", avatar: asset("faces/itsmeameliaa._.jpg") },
  { handle: "mr_kenling", followers: "3K", avatar: asset("faces/mr_kenling.jpg") },
  { handle: "dabrien3", followers: "9,659", avatar: asset("faces/dabrien3.jpg") },
];

/**
 * The "why join" centrepiece: real ambassadors on the supplied plate.
 *
 * ── How this got here ──────────────────────────────────────────────────────
 * Six earlier passes, and each ruled something out: a mocked dashboard
 * (invented numbers mean nothing), a table and then a bar chart (both read as a
 * leaderboard, the wrong argument), chips scattered loose with no container
 * (unstructured), a tile grid, and a hub-and-spoke network (scrapped). What is
 * left is the simplest thing that was always available: one clean card with the
 * five people listed in it.
 *
 * The reason this reads better than the earlier list attempts is not the layout
 * — it is that the avatars are now proper headshots rather than 150x150
 * Instagram thumbnails with a grey monogram among them.
 *
 * ── How the animation is built ─────────────────────────────────────────────
 * Pure CSS. Each row runs the same ambient lift on a shared duration with a
 * staggered negative delay, so it travels down the list and wraps without a
 * seam. No timer, no state, no effect — so nothing can desync, hydrate
 * differently from the server, or keep running after the section scrolls away.
 *
 * ⚠️  KEEP THIS COMPONENT STATELESS. The stagger is the entire animation. Driving
 * it from an interval would need cleanup on unmount and would put back the "one
 * row is special" reading that made the leaderboard versions wrong.
 */
const AmbassadorPanel = () => {
  const { t } = useTranslationHook();

  return (
    <div className={styles.panel}>
      <Image src={BACKDROP} alt="" className={styles.backdrop} width="100%" height="100%" />

      {/* The translucent bezel around the white card — it is what makes the card
        read as sitting ON the photograph rather than being pasted into a hole
        cut out of it. */}
      <div className={styles.rim}>
        <div className={styles.card}>
          <header className={styles.head}>
            <span className={styles.kicker}>{t("ambassador.v2.panel.kicker")}</span>
          </header>

          <ul className={styles.list}>
            {AMBASSADORS.map((person) => (
              <li className={styles.row} key={person.handle}>
                <Image
                  src={person.avatar}
                  alt=""
                  className={styles.face}
                  width="100%"
                  height="100%"
                />
                <span className={styles.handle}>@{person.handle}</span>
                <span className={styles.followers}>{person.followers}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(AmbassadorPanel, {
  componentName: "AmbassadorPanel-AmbassadorV2",
  showForChina: true,
});
